"use client";

/**
 * DepositWrapBanner — floating bottom-right card that surfaces when a
 * user has USDC.e or native USDC sitting on their DEPOSIT WALLET
 * (i.e. trading wallet) instead of being wrapped to pUSD.
 *
 * Why this exists:
 *   Polymarket V2 settles trades in pUSD. When a position wins, the
 *   redeem contract pays out in USDC.e (or native) — NOT pUSD. The
 *   backend `safe-deposit-monitor` is supposed to auto-wrap inbound
 *   USDC.e/native to pUSD, but it can miss events (RPC drops, lock
 *   contention, deploys). A user can also receive USDC.e via direct
 *   transfer from outside.
 *
 *   Until the funds are wrapped to pUSD, copytrading skips trades for
 *   that user (the worker reads `balance.pusd` only, post-wave-15).
 *   This banner gives them a one-click escape hatch: "Wrap all to
 *   trading balance (pUSD)" → POST /api/wallet/wrap-to-pusd → balance
 *   refreshes → copies resume on the next leader signal.
 *
 * UI/UX mirrors EoaSweepBanner exactly: fixed bottom-right card,
 * dismissable, pulsing dot, neon-yellow CTA. Different copy + endpoint.
 */

import { useState, useCallback } from "react";
import { toast } from "sonner";
import { authFetch } from "@/lib/authFetch";
import { usePolymarketBalanceContext } from "@/contexts/PolymarketBalanceContext";
import { useT } from "@/hooks/useT";

// Threshold below which we don't bother showing the banner. Matches
// the backend's DUST_USD_DEFAULT in safe-deposit-monitor.ts so the
// UX is consistent: tiny dust amounts (< $1) are not actionable.
const MIN_WRAP_USD = 1;

export default function DepositWrapBanner() {
  const { bridgedBalance, nativeBalance, walletType, depositWalletAddress, safeAddress, refetch } =
    usePolymarketBalanceContext();
  const t = useT();
  const [dismissed, setDismissed] = useState(false);
  const [running, setRunning] = useState(false);

  const usdce = bridgedBalance ?? 0;
  const native = nativeBalance ?? 0;
  const total = usdce + native;
  const hasTradingWallet = !!(depositWalletAddress || safeAddress);

  const handleWrap = useCallback(async () => {
    if (running) return;
    setRunning(true);

    try {
      const calls: Array<{ asset: 'usdce' | 'native'; amount: number; label: string }> = [];
      if (usdce >= MIN_WRAP_USD) calls.push({ asset: 'usdce', amount: usdce, label: `${usdce.toFixed(2)} USDC.e` });
      if (native >= MIN_WRAP_USD) calls.push({ asset: 'native', amount: native, label: `${native.toFixed(2)} USDC` });

      if (calls.length === 0) {
        toast.message("Nothing to wrap");
        await refetch();
        return;
      }

      // Sequential — wraps share the trading wallet and the relayer
      // batches per-asset. Parallel would race the Privy signing rate.
      const results: string[] = [];
      for (let i = 0; i < calls.length; i++) {
        const c = calls[i];
        toast.loading(
          calls.length > 1
            ? `Wrapping ${c.label} (${i + 1}/${calls.length})...`
            : `Wrapping ${c.label}...`,
          { id: 'deposit-wrap' },
        );
        const res = await authFetch('/api/wallet/wrap-to-pusd', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ asset: c.asset }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          // Surface the "missing approval" case loud — the user needs
          // to re-enable trading to add the CollateralOnramp approval
          // before wraps will go through.
          if (err?.missingApproval) {
            throw new Error(
              "Approval missing for the wrap contract. Re-enable trading from Settings to fix.",
            );
          }
          throw new Error(err?.error || `Wrap failed for ${c.label}`);
        }
        const data = await res.json();
        if (data?.txHash) results.push(data.txHash);
      }

      toast.success(
        results.length === calls.length
          ? t("wallet.wrapBanner.successWithAutoWrap")
          : t("wallet.wrapBanner.partialSuccess", { done: results.length, total: calls.length }),
        { id: 'deposit-wrap', duration: 8000 },
      );

      await refetch();
    } catch (e: any) {
      toast.error(e?.message || 'Wrap failed', { id: 'deposit-wrap' });
    } finally {
      setRunning(false);
    }
  }, [usdce, native, refetch, running]);

  // Show only when there's something meaningful to wrap AND the user
  // has a trading wallet set up. `walletType` may be null for legacy
  // EOA-only accounts — we still show the banner for them (they have
  // a safe_address). Dismiss state is local-only (won't persist across
  // reloads, which is intentional: a fresh win should re-surface it).
  if (dismissed) return null;
  if (!hasTradingWallet) return null;
  if (total < MIN_WRAP_USD) return null;
  if (usdce < MIN_WRAP_USD && native < MIN_WRAP_USD) return null;

  return (
    <div className="fixed right-4 bottom-[calc(52px+env(safe-area-inset-bottom)+0.75rem)] lg:bottom-4 z-[299] w-[calc(100vw-2rem)] sm:w-[380px] max-w-[380px]">
      <div className="bg-[#18181D] border border-[#2A2A30] rounded-[8px] shadow-2xl p-4 space-y-3">
        <div className="flex items-start gap-3">
          <span className="relative flex h-2 w-2 flex-shrink-0 mt-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-[#CCFF00] opacity-60 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#CCFF00]" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-200 leading-snug">
              <span className="font-semibold text-white">${total.toFixed(2)}</span>{" "}
              {t("wallet.wrapBanner.body")}
            </p>
            {(usdce > 0 || native > 0) && (
              <p className="text-[11px] text-gray-500 mt-1">
                {usdce > 0 && <span>{usdce.toFixed(2)} USDC.e</span>}
                {usdce > 0 && native > 0 && <span> + </span>}
                {native > 0 && <span>{native.toFixed(2)} USDC</span>}
              </p>
            )}
            <p className="text-[11px] text-gray-400 mt-1.5 leading-snug">
              {t("wallet.wrapBanner.autoWrapInfo")}
            </p>
          </div>
          <button
            onClick={() => setDismissed(true)}
            aria-label="Dismiss"
            className="text-gray-500 hover:text-white text-lg leading-none -mt-1 flex-shrink-0"
          >
            ×
          </button>
        </div>
        <button
          onClick={handleWrap}
          disabled={running}
          className="w-full px-4 py-2 rounded-[5px] bg-[#CCFF00] text-[#0f0f12] text-sm font-bold disabled:opacity-60 disabled:cursor-not-allowed hover:bg-[#b8e600] transition-colors"
        >
          {running ? t("wallet.wrapBanner.wrapping") : t("wallet.wrapBanner.cta")}
        </button>
      </div>
    </div>
  );
}
